import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AIListBuilderViewModel } from '@app/lists/model/ai-list-builder-view.model';
import { AIListProductCardViewModel } from '@app/lists/model/ai-list-product-card.model';
import { AIListBuilderService } from '@app/lists/services/ai-list-builder.service';
import { ViewWillEnter, ViewWillLeave, MenuController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { map, Observable, of } from 'rxjs';
import { Product } from '@shared/models/product.model';
import { LoadingState } from '@usf/ngrx-product';
import { FilterOption } from '@shared/components/filter-modal/filter-modal.component';
import { IAIListFilterState } from '@usf/ngrx-list-ai';
import { ListsOverlayService } from '@app/lists/services/lists-overlay.service';

@Component({
  selector: 'app-ai-list',
  templateUrl: './ai-list.page.html',
  styleUrls: ['./ai-list.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiListPage implements ViewWillEnter, ViewWillLeave {
  viewModel$: Observable<AIListBuilderViewModel>;
  searchKey = '';
  previousSearchKey = '';
  platformRef = PlatformEnum;
  currentIngredient: AIListProductCardViewModel;

  ingredientsWithSelectionInfo$: Observable<{
    ingredients: Product[];
    matchingProductNumber: number | null;
  }>;
  loadingState$: Observable<LoadingState>;
  aogSearchResultsLoaded$: Observable<boolean>;
  aogSearchResultsLoading$: Observable<boolean>;
  selectedProduct$: Observable<Product | null>;

  constructor(
    private readonly panAppState: PanAppState,
    private readonly translateService: TranslateService,
    private readonly aiListBuilderService: AIListBuilderService,
    readonly platformService: PlatformService,
    private readonly menuController: MenuController,
    private readonly listsOverlayService: ListsOverlayService,
  ) {
    this.viewModel$ = this.aiListBuilderService.aiListBuilderViewModel$;

    this.ingredientsWithSelectionInfo$ =
      this.aiListBuilderService.getAlternativeIngredientsWithSelectionInfo();

    this.loadingState$ =
      this.aiListBuilderService.selectAlternativeIngredientsLoading();

    this.selectedProduct$ = this.aiListBuilderService.selectedProduct$;

    this.aogSearchResultsLoading$ = this.aiListBuilderService
      .selectAogSearchResultsLoading()
      .pipe(map((state: any) => state.loading));
    this.aogSearchResultsLoaded$ = this.aiListBuilderService
      .selectAogSearchResultsLoading()
      .pipe(map((state: any) => state.loaded));
  }

  ionViewWillEnter(): void {
    this.panAppState.setPageTitle(
      this.translateService.instant('i18n.lists.aiListBuilder'),
    );
  }

  ionViewWillLeave(): void {
    this.clearFilters();
  }

  trackItem(index: number, item: AIListProductCardViewModel) {
    return item ? item.aiListItem?.pimInfo + index : index;
  }

  async openIngredientMenu(item: AIListProductCardViewModel) {
    this.aiListBuilderService.clearSelectedProduct();
    this.aiListBuilderService.clearCurrentIngredient();

    // Store the current ingredient
    this.currentIngredient = item;

    // Reset filter options to default state when opening menu
    this.aiListBuilderService.resetIngredientMenuSearch();
    this.aiListBuilderService.updateMenuFilterOptions([]);

    // Set the current product in the service for reordering
    if (item.product) {
      // Make sure to clear any existing selection first
      this.aiListBuilderService.clearCurrentIngredient();
      // Then set the new product
      this.aiListBuilderService.setCurrentIngredient(item.product);
    } else {
      this.aiListBuilderService.clearCurrentIngredient();
    }

    // Ensure any other open menus are closed first
    await this.menuController.enable(true, 'ingredient-menu');

    await this.menuController.open('ingredient-menu');

    this.aiListBuilderService.searchAltIngredient({
      ingredientName:
        item.product?.summary?.productDescTxtl || item.aiListItem?.sourceText,
      pimInfo: item.aiListItem?.pimInfo,
    });
  }

  async closeIngredientMenu(): Promise<void> {
    this.aiListBuilderService.clearCurrentIngredient();
    this.aiListBuilderService.clearSelectedProduct();

    await this.menuController.close('ingredient-menu');

    // Reset filter options to default state when closing menu
    this.aiListBuilderService.resetIngredientMenuSearch();
    this.aiListBuilderService.updateMenuFilterOptions([]);
  }

  /**
   * Handles saving the selected ingredient
   */
  async saveIngredientSelection(selectedProduct: Product): Promise<void> {
    // In a real app, you would update the ingredient in the database/store
    await this.closeIngredientMenu();
  }

  /**
   * Handles searching in the list
   * @param event - The search event
   */
  onSearch(event: any) {
    if (this.searchKey !== this.previousSearchKey) {
      this.aiListBuilderService.searchInList(this.searchKey);
      this.previousSearchKey = this.searchKey;
    }
  }

  resetSearch() {
    this.searchKey = '';
    this.aiListBuilderService.resetSearch();
  }

  /**
   * Opens the filter modal with the provided options
   * @param filterOptions - The filter options to display in the modal
   */
  async openFilterModal(filterOptions: FilterOption[]) {
    const modal =
      await this.listsOverlayService.openAiFilterModal(filterOptions);

    const { data } = await modal.onDidDismiss();
    if (data && data.action !== 'close') {
      const filterState: IAIListFilterState = {
        direct: false,
        earlyCutoff: false,
        discontinuedWhenOut: false,
        exclusiveBrands: false,
      };

      for (const option of data.options) {
        if (option.selected) {
          filterState[option.id as keyof IAIListFilterState] = true;
        }
      }
      this.aiListBuilderService.updateFilterOptions(filterState);
    }
  }

  /**
   * Clears all applied filters
   */
  clearFilters() {
    this.aiListBuilderService.updateFilterOptions({
      direct: false,
      earlyCutoff: false,
      discontinuedWhenOut: false,
      exclusiveBrands: false,
    } as IAIListFilterState);
  }
}
